<template>
    <v-data-table class="elevation-0"
                  :headers="headers"
                  :items="items"
                  :items-per-page="15"
                  :footer-props="dataTableFooterProps"
                  :search="dataTableSearch"
                  :custom-filter="dataTableCustomFilter"
                  :loading="isBusy"
                  :loading-text="$t('common.loading_table_data')"
    >
        <template v-slot:item.roles_text="{ item }">
            {{ item.roles_text }}
        </template>
        <template v-slot:item.action="{ item }">
            <span class="text-no-wrap">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-icon small class="mr-2"
                                @click="onUpdate(item)"
                                v-on="on"
                        >
                            mdi-pencil
                        </v-icon>
                    </template>
                    <span>{{ $t('tooltip.user.update') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-icon small class="mr-2"
                                @click="onWarehouses(item)"
                                :disabled="!isBuyer(item) && !isApprover(item)"
                                v-on="on"
                        >
                            mdi-warehouse
                        </v-icon>
                    </template>
                    <span>{{ $t('tooltip.user.warehouses') }}</span>
                </v-tooltip>

                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-icon small
                                @click="onDelete(item)"
                                :disabled="isMe(item)"
                                v-on="on"
                        >
                            mdi-delete
                        </v-icon>
                    </template>
                    <span>{{ $t('tooltip.user.delete') }}</span>
                </v-tooltip>
            </span>
        </template>
        <template v-slot:top>
            <v-toolbar flat color="white">
                <v-text-field
                    v-model="dataTableSearch"
                    prepend-inner-icon="mdi-magnify"
                    :placeholder="$t('common.filter')"
                    outlined
                    dense
                    hide-details
                />
                <v-spacer></v-spacer>
                <v-dialog v-model="dialog" max-width="500">
                    <template v-slot:activator="{ on }">
                        <v-btn color="success"
                               dark
                               class="mb-2"
                               v-on="on"
                               @click="cleanEditedItem"
                        >
                            {{ $t('button.new_user') }}
                        </v-btn>
                    </template>
                    <v-card>
                        <v-card-title>
                            <span class="headline">{{ dialogTitle }}</span>
                            <v-spacer/>
                            <v-btn @click="dialog = false" icon>
                                <v-icon>mdi-window-close</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-card-text>
                            <v-container>
                                <span class="title">{{ $t('users.personal_info') }}</span>
                                <v-row>
                                    <v-col cols="12" md="6">
                                        <v-text-field v-model="$v.editedItem.first_name.$model"
                                                      :label="$t('users.first_name')"
                                                      :error-messages="firstNameErrors"
                                                      @input="$v.editedItem.first_name.$touch()"
                                                      @blur="$v.editedItem.first_name.$touch()"
                                                      class="required"
                                                      required
                                        />
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-text-field v-model="$v.editedItem.last_name.$model"
                                                      :label="$t('users.last_name')"
                                                      :error-messages="lastNameErrors"
                                                      @input="$v.editedItem.last_name.$touch()"
                                                      @blur="$v.editedItem.last_name.$touch()"
                                                      class="required"
                                                      required
                                        />
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-text-field v-model="$v.editedItem.username.$model"
                                                      :label="$t('users.username')"
                                                      :error-messages="usernameErrors"
                                                      @input="$v.editedItem.username.$touch()"
                                                      @blur="$v.editedItem.username.$touch()"
                                                      class="required"
                                                      required
                                        />
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-text-field v-model="$v.editedItem.password.$model"
                                                      :label="$t('users.password')"
                                                      :error-messages="passwordErrors"
                                                      :append-icon="showPassword ? 'mdi-eye':'mdi-eye-off'"
                                                      :type="showPassword ? 'text' : 'password'"
                                                      @click:append="showPassword = !showPassword"
                                                      @input="$v.editedItem.password.$touch()"
                                                      @blur="$v.editedItem.password.$touch()"
                                                      :class="{required: editedIndex === -1}"
                                                      required
                                        />
                                    </v-col>
                                    <v-col cols="12" md="12">
                                        <v-text-field v-model="$v.editedItem.email.$model"
                                                      :label="$t('users.email')"
                                                      :error-messages="emailErrors"
                                                      @input="$v.editedItem.email.$touch()"
                                                      @blur="$v.editedItem.email.$touch()"
                                                      class="required"
                                                      required
                                        />
                                    </v-col>
                                </v-row>
                            </v-container>
                            <v-divider></v-divider>
                            <v-container>
                                <span class="title">{{ $t('users.user_role') }}</span>
                                <v-row>
                                    <v-col cols="12" md="6">
                                        <v-checkbox v-model="$v.editedItem.is_admin.$model"
                                                    :label="$t('users.admin')"
                                                    :true-value="true"
                                                    :false-value="false"
                                                    hide-details
                                        />
                                        <v-checkbox v-model="$v.editedItem.is_approver.$model"
                                                    :label="$t('users.approver')"
                                                    :true-value="true"
                                                    :false-value="false"
                                                    hide-details
                                        />
                                        <v-checkbox v-model="$v.editedItem.is_buyer.$model"
                                                    :label="$t('users.buyer')"
                                                    :true-value="true"
                                                    :false-value="false"
                                                    hide-details
                                        />
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn color="blue darken-1" text @click="onClose">
                                {{ $t('button.close') }}
                            </v-btn>
                            <v-btn color="blue darken-1" text @click="onSave">
                                {{ $t('button.save') }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-toolbar>
        </template>
    </v-data-table>
</template>

<script>
import { API } from "@/api/api";
import SnackbarMixin from "@/mixins/SnackbarMixin";
import DataTableMixin from "@/mixins/DataTableMixin";
import { validationMixin } from "vuelidate";
import { email, required, requiredIf } from "vuelidate/lib/validators";

export default {
    name: 'UsersTableComponent',
    mixins: [SnackbarMixin, DataTableMixin, validationMixin],
    data: () => ({
        // loading
        isBusyUsers: false,
        isBusyCreate: false,
        isBusyUpdate: false,
        isBusyDelete: false,
        // table data
        items: [],
        // create/update dialog
        dialog: false,
        // edited user
        editedItem: {
            first_name: null,
            last_name: null,
            username: null,
            password: null,
            email: null,
            is_admin: false,
            is_approver: false,
            is_buyer: false,
        },
        defaultItem: {
            first_name: null,
            last_name: null,
            username: null,
            password: null,
            email: null,
            is_admin: null,
            is_approver: null,
            is_buyer: null,
        },
        editedIndex: -1,
        // show password in dialog
        showPassword: false
    }),
    validations: {
        editedItem: {
            first_name: { required },
            last_name: { required },
            username: { required },
            password: {
                requiredIf: requiredIf(function () {
                    return this.editedIndex === -1; // required on create, not when update
                })
            },
            email: { required, email },
            is_admin: {},
            is_approver: {},
            is_buyer: {}
        }
    },
    computed: {
        isBusy () {
            return this.isBusyUsers || this.isBusyCreate || this.isBusyUpdate || this.isBusyDelete;
        },
        dialogTitle () {
            return this.editedIndex === -1 ? this.$t('users.new_user') : this.$t('users.edit_user')
        },
        headers () {
            return [
                {
                    text: this.$t('users.first_name'),
                    value: 'first_name'
                },
                {
                    text: this.$t('users.last_name'),
                    value: 'last_name'
                },
                {
                    text: this.$t('users.username'),
                    value: 'username'
                },
                {
                    text: this.$t('users.email'),
                    value: 'email'
                },
                {
                    text: this.$t('users.user_role'),
                    value: 'roles_text'
                },
                {
                    text: '',
                    value: 'action',
                    align: 'end',
                    sortable: false
                }
            ];
        },
        firstNameErrors () {
            const errors = [];
            if (!this.$v.editedItem.first_name.$dirty) return errors;
            !this.$v.editedItem.first_name.required && errors.push(this.$t('validation.first_name.required'));
            return errors;
        },
        lastNameErrors () {
            const errors = [];
            if (!this.$v.editedItem.last_name.$dirty) return errors;
            !this.$v.editedItem.last_name.required && errors.push(this.$t('validation.last_name.required'));
            return errors;
        },
        usernameErrors () {
            const errors = [];
            if (!this.$v.editedItem.username.$dirty) return errors;
            !this.$v.editedItem.username.required && errors.push(this.$t('validation.username.required'));
            return errors;
        },
        passwordErrors () {
            const errors = [];
            if (!this.$v.editedItem.password.$dirty) return errors;
            !this.$v.editedItem.password.requiredIf && errors.push(this.$t('validation.password.required'));
            return errors;
        },
        emailErrors () {
            const errors = [];
            if (!this.$v.editedItem.email.$dirty) return errors;
            !this.$v.editedItem.email.required && errors.push(this.$t('validation.email.required'));
            !this.$v.editedItem.email.email && errors.push(this.$t('validation.email.email'));
            return errors;
        },
        roleErrors () {
            const errors = [];
            if (!this.$v.editedItem.role.$dirty) return errors;
            !this.$v.editedItem.role.required && errors.push(this.$t('validation.role.required'));
            return errors;
        }
    },
    methods: {
        rolesString (roles) {
            let rolesString = [];
            roles.forEach(role => {
                if (role === 'ROLE_ADMIN') {
                    rolesString.push(this.$t('users.admin'));
                }
                if (role === 'ROLE_APPROVER') {
                    rolesString.push(this.$t('users.approver'));
                }
                if (role === 'ROLE_BUYER') {
                    rolesString.push(this.$t('users.buyer'));
                }
            });
            return rolesString.length > 0 ? rolesString.join(', ') : '-';
        },
        fetchUsers () {
            this.isBusyUsers = true;
            API.get('/api/users')
                .then(response => {
                    this.items = response.data.map(user => this.withDataTableSearch({
                        ...user,
                        roles_text: this.rolesString(user.roles)
                    }, [
                        user.first_name,
                        user.last_name,
                        user.username,
                        user.email,
                        this.rolesString(user.roles)
                    ]));
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyUsers = false;
                })
        },
        onWarehouses (item) {
            this.$router.push({ name: 'warehouses-to-user', params: { userId: item.id }});
        },
        onUpdate (item) {
            this.editedIndex = this.items.indexOf(item);
            this.editedItem = Object.assign({}, item);
            this.$set(this.editedItem, 'is_admin', item.roles.includes('ROLE_ADMIN'));
            this.$set(this.editedItem, 'is_approver', item.roles.includes('ROLE_APPROVER'));
            this.$set(this.editedItem, 'is_buyer', item.roles.includes('ROLE_BUYER'));
            this.dialog = true;
        },
        onDelete (item) {
            if (!confirm(this.$t('confirm.delete_user', { user: item.first_name + ' ' + item.last_name }))) {
                return;
            }
            this.isBusyDelete = true;
            API.delete('/api/users/' + encodeURI(item.id))
                .then(() => {
                    this.snackbarSuccess(this.$t('snackbar.user.deleted'));
                    this.fetchUsers();
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyDelete = false;
                });
        },
        onClose () {
            this.dialog = false;
            setTimeout(() => {
                this.cleanEditedItem();
            }, 300);
        },
        isMe (user) {
            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }
            return userId === user.id;
        },
        isBuyer (user) {
            return user.roles.includes('ROLE_BUYER');
        },
        isApprover (user) {
            return user.roles.includes('ROLE_APPROVER');
        },
        onSave () {
            this.$v.editedItem.$touch();
            if (this.$v.editedItem.$anyError) {
                return;
            }

            let data = {
                first_name: this.editedItem.first_name,
                last_name: this.editedItem.last_name,
                username: this.editedItem.username,
                password: this.editedItem.password,
                email: this.editedItem.email,
                is_admin: this.editedItem.is_admin,
                is_approver: this.editedItem.is_approver,
                is_buyer: this.editedItem.is_buyer,
            };

            if (this.editedIndex > -1) {
                // update
                this.isBusyUpdate = true;
                API.patch('/api/users/' + encodeURI(this.items[this.editedIndex].id), data)
                    .then(() => {
                        this.onClose();
                        this.snackbarSuccess(this.$t('snackbar.user.updated'));
                        this.fetchUsers();
                    })
                    .catch(error => {
                        this.snackbarError(error);
                    })
                    .finally(() => {
                        this.isBusyUpdate = false;
                    })
            } else {
                // create
                this.isBusyCreate = true;
                API.post('/api/users', data)
                    .then(() => {
                        this.onClose();
                        this.snackbarSuccess(this.$t('snackbar.user.created'));
                        this.fetchUsers();
                    })
                    .catch(error => {
                        this.snackbarError(error);
                    })
                    .finally(() => {
                        this.isBusyCreate = false;
                    });
            }
        },
        cleanEditedItem () {
            this.$v.editedItem.$reset();
            this.editedItem = Object.assign({}, this.defaultItem);
            this.editedIndex = -1;
        }
    },
    created () {
        this.fetchUsers();
    }
}
</script>

<style lang="scss">
div.v-input.required {
    label:after {
        content: " *";
        color: red;
    }
}
</style>
